import { Component, computed, DestroyRef, HostListener, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { CommonModule } from '@angular/common';
import { ErrorMessageService } from 'src/app/services/error-message.util';
import { TranslateModule } from '@ngx-translate/core';
import { ModalNames } from 'src/app/common/modal.config';
import { ModalService } from 'src/app/services/modal.service';
import { LoginService } from 'src/app/services/login.service';
import { strictEmailValidator } from 'src/app/utils/validators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormDataStoreService } from 'src/app/services/form-data.store';

declare const google: any;

@Component({
  selector: 'app-login-popup',
  templateUrl:  `./${environment.pageFolder}/login-popup.${environment.pageFolder}.component.html`,
  styleUrls:  [`./${environment.pageFolder}/login-popup.${environment.pageFolder}.component.sass`],
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, TranslateModule]
})

export class LoginPopupComponent {
  private readonly formDataKey = `${ModalNames.Login}`;
  loginForm = this.formBuilder.group({
    username: ['', [ Validators.required, strictEmailValidator ]],
    password: ['', [ Validators.required ]],
  });
  errorMessage: string = "";
  showPassword: boolean = false;

  public readonly modalNamesEnum = ModalNames;
  readonly isLoginModalOpen = computed(() => this.modalService.modalsState().get(ModalNames.Login) ?? false);
  private readonly destroyRef = inject(DestroyRef);

  constructor( 
    private readonly formBuilder: FormBuilder,
    public modalService: ModalService,
    public loginService: LoginService,
    private readonly errorMessageService: ErrorMessageService,
    private readonly formDataStore: FormDataStoreService
  ) {}
  
  async ngOnInit() {
    this.formDataStore.subscribeToForm(this.formDataKey, this.loginForm, this.destroyRef);

    this.loginService.error$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((message) => {
      this.errorMessage = message;
    });
  
    await this.loginService.loadFacebookSDK();
  }

  ngAfterViewInit(): void {      
    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (response: any) => this.loginService.handleCredentialResponse(response)
    });
        
    google.accounts.id.renderButton(
      document.getElementById("googleBtn"),
      {
        theme: "outline",
        size: "large"
      }
    );
  }

  onSubmit(): void {
    const username = this.loginForm.get('username')!;
    const password = this.loginForm.get('password')!;

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();

      if (username.hasError('required') || password.hasError('required')) {
        this.errorMessage = this.errorMessageService.getErrorMessage('allFieldsRequired');
      } else if (username.hasError('invalidEmail')) {
        this.errorMessage = this.errorMessageService.getErrorMessage('invalidEmailFormat');
      } else {
        this.errorMessage = this.errorMessageService.getErrorMessage('error');
      }

      return;
    }
    
    const loader = document.getElementById("loader-container")!;
    loader.style.display = "flex";

    this.loginService.login({ username: username.value, password: password.value }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: data => {
        if (!data.token) {
          loader.style.display = "none";
          this.errorMessage = this.errorMessageService.getErrorMessage('dataDoesntMatch');
        }

        this.formDataStore.clearFormData(this.formDataKey);
      },
      error: () => {
        loader.style.display = "none";
        this.errorMessage = this.errorMessageService.getErrorMessage('error');
      }
    });
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(_: KeyboardEvent) {
    if (this.isLoginModalOpen()) {
      this.modalService.closeModal(ModalNames.Login);
    }
  }
}